import * as React from 'react';
import Alert from 'react-bootstrap/Alert';
import { Link, Params, useParams } from 'react-router-dom';
import { timer } from 'rxjs';

import { LoadingComponent } from '../loading/loading.component';
import {
  SubscribingEntity,
  useSubscribingEntity,
} from '../shared/subscribing-entity';
import { clx, withHooks, formatDate } from '../shared/utils';
import {
  LocalManifest,
  LocalManifestReport,
  ManifestState,
  ReportState,
  ManifestService,
  useManifestService,
} from '../services/manifest.service';
import { useAlertService, AlertService } from '../services/alert.service';

type ReportListingProps = {
  alertService: AlertService;
  manifestService: ManifestService;
  reportFilterUri: string | undefined;
  subscribingEntity: SubscribingEntity;
};

type ReportListingState = {
  hide: boolean;
  loading: boolean;
  reports: LocalManifestReport[];
};

class ReportListing extends React.Component<
  ReportListingProps,
  ReportListingState
> {
  constructor(props: ReportListingProps) {
    super(props);
    this.state = {
      hide: true,
      loading: true,
      reports: [],
    };
  }

  componentDidMount() {
    this.subscribeToManifest();
  }

  private subscribeToManifest(): void {
    this.props.subscribingEntity.addSubscriptionToCloseOnDestroy(
      this.props.manifestService.getReportManifest().subscribe({
        next: (response: LocalManifest) => {
          if (response && response.manifestState !== ManifestState.Initial) {
            this.handleReportManifest(response);
            this.setState({ loading: false });
          }
        },
        error: (err) => {
          this.setState({ loading: false });
          this.props.alertService.addAlertToList({
            level: 'danger',
            message:
              'We were unable to retrieve a listing of available reports. Please notify your account manager.',
            dismissible: true,
          });
          throw new Error(err.message);
        },
      })
    );
    this.props.subscribingEntity.addSubscriptionToCloseOnDestroy(
      this.props.manifestService.getManifestErrors().subscribe({
        next: (er: any) => {
          this.setState({ loading: false });
          this.props.alertService.addAlertToList({
            level: 'danger',
            message:
              'We were unable to retrieve a listing of available reports. Please notify your account manager.',
            dismissible: true,
          });
          throw new Error(er);
        },
      })
    );
    this.props.subscribingEntity.addSubscriptionToCloseOnDestroy(
      timer(35000).subscribe(() => {
        if (this.state.loading) {
          this.setState({ loading: false });
          this.props.alertService.addAlertToList({
            level: 'danger',
            message:
              'We were unable to retrieve a listing of available reports. Please notify your account manager.',
            dismissible: true,
          });
        }
      })
    );
  }

  private handleReportManifest(response: LocalManifest): void {
    let reports = response.reports.slice().sort((a, b) => {
      if (a.reportDisplayName < b.reportDisplayName) {
        return -1;
      } else if (a.reportDisplayName > b.reportDisplayName) {
        return 1;
      }
      return 0;
    });
    if (reports.length > 0) {
      if (this.props.reportFilterUri != null) {
        const filtered: LocalManifestReport[] = reports.filter((listing) => {
          return listing.reportUri === this.props.reportFilterUri;
        });
        if (filtered.length > 0) {
          reports = filtered;
        } else {
          this.props.alertService.addAlertToList({
            level: 'warning',
            message:
              'The requested report is no longer available. All available reports shown instead. If you believe this is an error, please notify your account manager.',
            dismissible: true,
          });
        }
      }
      this.setState({
        reports,
        hide: false,
      });
    }
  }

  getReport(report: LocalManifestReport, evt: React.SyntheticEvent): void {
    evt.preventDefault();
    this.props.manifestService.getReport(report);
  }

  render() {
    const { loading, hide, reports } = this.state;

    return (
      <>
        <div className="row">
          <div className="col-auto">
            <h2 className="h3 mb-0 mt-0">Reports</h2>
            <p className="subdued small">
              The following reports are available for your account.
            </p>
          </div>
        </div>

        {!loading && (!reports || reports?.length === 0) && (
          <div className="alert-container">
            <Alert variant="info" dismissible={false}>
              No reports are currently available for your account. If you
              believe this is an error, please notify your account manager.
            </Alert>
          </div>
        )}

        <div aria-hidden={hide} className={clx('listing', { hidden: hide })}>
          <div className="row row-cols-1">
            <div className="col mb-2">
              <div className="card d-flex flex-column h-100 p-3 shadow stat">
                <div className="card-body d-flex flex-column">
                  <div className="card-text">
                    <table className="table table-hover report-listing">
                      <thead>
                        <tr>
                          <th scope="col">Report Name</th>
                          <th scope="col">Report Type</th>
                          <th scope="col">Report State</th>
                          <th scope="col">Update Date</th>
                          <th scope="col">Expiration Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reports.map((listing) => (
                          <tr key={listing.id} id={`id-${listing.id}`}>
                            <td>
                              {listing.state === ReportState.Ready ? (
                                <a
                                  onClick={(evt) =>
                                    this.getReport(listing, evt)
                                  }
                                  href={document.location.href}
                                  title={`Click to download the ${listing.reportDisplayName} report for '${listing.reportDisplayName}'.`}
                                >
                                  {listing.reportDisplayName}
                                </a>
                              ) : (
                                listing.reportDisplayName
                              )}
                            </td>
                            <td>{listing.reportTypeDescription}</td>
                            <td>{listing.state}</td>
                            <td>{formatDate(listing.generationDate)}</td>
                            <td>{formatDate(listing.expireOnDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading && <LoadingComponent />}

        <div className="row">
          <div className="col-auto">
            <h2 className="h3 mb-0 mt-0">Request a Report</h2>
            <p className="subdued small">
              <Link
                to={'/instructor-courses'}
                aria-label="go to a list of my courses"
                id="go-to-courses"
              >
                Click here to see a list of MindTap courses to request a MindTap
                student interaction log.
              </Link>
            </p>
          </div>
        </div>
      </>
    );
  }
}

export const ReportListingComponent = withHooks<ReportListingProps, {}>(
  [useAlertService, 'alertService'],
  [useManifestService, 'manifestService'],
  [
    useParams,
    (params) => ({
      reportFilterUri: (params as Params).reportUri,
    }),
  ],
  [useSubscribingEntity, 'subscribingEntity']
)(ReportListing);
